import React, { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';

// Provider-specific entry points prevent a URL's signed query from accidentally
// selecting a different provider through ReactPlayer's substring URL patterns.
const YouTubePlayer = lazy(() => import('react-player/youtube'));
const FilePlayer = lazy(() => import('./GalaxyFilePlayer'));
const LOAD_DEADLINE_MS = 15000;

export default function GalaxyVideoPlayer({ media, autoPlay = false, onPause, onPlaying }) {
  const player = useRef(null);
  const alive = useRef(true);
  const [playing, setPlaying] = useState(autoPlay);
  const [muted, setMuted] = useState(autoPlay);
  const [status, setStatus] = useState(autoPlay ? 'attempting' : 'loading');
  const [attempt, setAttempt] = useState(0);
  const [errorText, setErrorText] = useState('');
  const callbacks = useRef({ onPause, onPlaying });
  callbacks.current = { onPause, onPlaying };
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  useEffect(() => {
    if (!['loading', 'attempting', 'buffering'].includes(status)) return undefined;
    const timer = setTimeout(() => {
      if (!alive.current) return;
      setPlaying(false);
      setStatus(status === 'attempting' ? 'blocked' : 'error');
      setErrorText('The video did not become ready. Check the link or retry.');
    }, LOAD_DEADLINE_MS);
    return () => clearTimeout(timer);
  }, [status, attempt]);
  const blocked = () => {
    if (!alive.current) return;
    setPlaying(false); setStatus('blocked'); callbacks.current.onPause?.();
  };
  const config = useMemo(() => {
    const nativeHls = media.kind === 'hls' && typeof document !== 'undefined' &&
      Boolean(document.createElement('video').canPlayType('application/vnd.apple.mpegurl'));
    return {
      youtube: { playerVars: { rel: 0 }, onUnstarted: () => {
        if (alive.current) { setPlaying(false); setStatus('blocked'); callbacks.current.onPause?.(); }
      } },
      file: { forceVideo: true, forceHLS: media.kind === 'hls' && !nativeHls,
        forceDisableHls: nativeHls, forceDASH: media.kind === 'dash',
        attributes: { playsInline: true, preload: 'metadata',
          // Keep signed direct paths intact, including hosts with built-in rewrites.
          ...((media.kind === 'file' || nativeHls) ? { src: media.url } : {}),
          onVolumeChange: (event) => { if (alive.current) setMuted(event.currentTarget.muted); },
        } },
    };
  }, [media.kind, media.url]);
  const Player = media.kind === 'youtube' ? YouTubePlayer : FilePlayer;
  const play = () => { setErrorText(''); setStatus('attempting'); setPlaying(true); };
  const pause = () => { setPlaying(false); setStatus('paused'); callbacks.current.onPause?.(); };
  const sound = () => {
    setMuted(false);
    const internal = player.current?.getInternalPlayer?.();
    if (internal && 'muted' in internal) { internal.muted = false; if (!internal.volume) internal.volume = 1; }
    else internal?.unMute?.();
    if (!playing) play();
  };
  const labels = { loading: 'Loading video…', ready: 'Ready · paused', attempting: 'Attempting playback…',
    playing: muted ? 'Playing · muted' : 'Playing', buffering: 'Buffering…', paused: 'Paused',
    blocked: 'Playback needs your permission. Press Play.', error: errorText || 'Video unavailable. Check access, expiry, or the video format.' };
  return <div className="galaxy-video-player">
    <div className={`galaxy-video-frame${media.kind === 'youtube' ? ' galaxy-video-frame--youtube' : ''}`}>
      <Suspense fallback={<span className="galaxy-video-loading">Loading player…</span>}>
        <Player key={attempt} ref={player} url={media.url} config={config}
          width="100%" height="100%" controls playsinline loop={false} stopOnUnmount
          playing={playing} muted={muted}
          onProgress={() => {
            // YouTube does not emit the native volumechange event. Reuse the
            // installed player's progress callback to reflect its own controls.
            const internal = player.current?.getInternalPlayer?.();
            if (alive.current && typeof internal?.isMuted === 'function') setMuted(internal.isMuted());
          }}
          onReady={() => { if (alive.current) setStatus((value) => value === 'loading' ? 'ready' : value); }}
          onPlay={() => {
            if (!alive.current) return;
            // Mirror native controls so ReactPlayer 2 observes true -> false on Pause.
            setPlaying(true); setStatus('playing'); callbacks.current.onPlaying?.();
          }}
          onPause={() => { if (alive.current) pause(); }}
          onEnded={() => { if (alive.current) pause(); }}
          onBuffer={() => { if (alive.current) setStatus((value) => value === 'playing' ? 'buffering' : value); }}
          onBufferEnd={() => { if (alive.current) setStatus((value) => value === 'buffering' ? 'playing' : value); }}
          onError={(error) => {
            if (!alive.current) return;
            if (error?.name === 'NotAllowedError') { blocked(); return; }
            setPlaying(false); setStatus('error'); callbacks.current.onPause?.();
            // Provider errors can include delivery tokens. Never render/log them.
            setErrorText(typeof error === 'number' && [101, 150].includes(error)
              ? 'This video does not allow embedded playback. Choose another link.'
              : 'Video unavailable. Check access, expiry, or the video format.');
          }} />
      </Suspense>
    </div>
    <div className="galaxy-video-player__status">
      <span role="status">{labels[status]}</span>
      {['attempting', 'buffering'].includes(status) && <button type="button" onClick={pause}>Pause</button>}
      {status === 'blocked' && <button type="button" onClick={play}>Play video</button>}
      {muted && <button type="button" onClick={sound}>Turn sound on</button>}
      {status === 'error' && <button type="button" onClick={() => {
        setPlaying(false); setStatus('loading'); setErrorText(''); setAttempt((value) => value + 1);
      }}>Retry video</button>}
    </div>
  </div>;
}
